import Locales from 'UdeskLocales';
import React, { useState, useEffect, useCallback, useMemo, FC, memo } from 'react';
import { useRequest } from 'ahooks';
import { useAudio } from './hooks';
import useResizeObserver from 'use-resize-observer';
import { PageHeader, Space, Radio } from 'udesk-ui';
import ChatRecord from 'Component/common/chat-record';
import UdeskDatePicker from 'Component/common/udesk-date-picker';
import Filter from './components/Filter';
import { getFeishuSessionsMsgHistory } from 'src/api/feishuSessions/msg/history';
import { getFeishuSessionsMsgToday } from 'src/api/feishuSessions/msg/today';
import { GetFeishuSessionsMsgHistoryQuery } from 'src/api/types';
import moment from 'moment';
import './style.scss';

interface TemplateProps {}

const Template: FC<TemplateProps> = memo(() => {
    const locales = Locales['current'];

    const [params, setParams] = useState<Partial<GetFeishuSessionsMsgHistoryQuery>>({
        pageNum: 1,
        pageSize: 20,
        startTime: moment().subtract('days', 30).format('YYYY-MM-DD') + ' 00:00:00',
        endTime: moment().format('YYYY-MM-DD') + ' 23:59:59',
    });
    const [data, setData] = useState<any[]>([]);

    const onChartChange = (callId) => {
        if (parseInt(callId, 10) !== params.sessionId) {
            setParams({
                pageNum: 1,
                pageSize: 20,
                startTime: moment().subtract('days', 30).format('YYYY-MM-DD') + ' 00:00:00',
                endTime: moment().format('YYYY-MM-DD') + ' 23:59:59',
                sessionId: parseInt(callId, 10),
            });
            setData([]);
        }
    };

    const [mesgType, setMesgType] = useState<'today' | 'history'>('today');

    const TABS_OPTIONS = useMemo(
        () => [
            { label: locales.pages.wechatQa.chartRecord.detail.today, value: 'today' },
            { label: locales.pages.wechatQa.chartRecord.detail.history, value: 'history' },
        ],
        [
            locales.pages.wechatQa.chartRecord.detail.history,
            locales.pages.wechatQa.chartRecord.detail.today,
        ]
    );

    const onMesgTypeChange = useCallback((e) => {
        setMesgType(e.target.value);
        setParams((prev) => {
            return {
                ...prev,
                pageNum: 1,
                pageSize: 20,
                startTime: moment().subtract('days', 30).format('YYYY-MM-DD') + ' 00:00:00',
                endTime: moment().format('YYYY-MM-DD') + ' 23:59:59',
            };
        });
        setData([]);
    }, []);

    const { playRudio, isPlaying } = useAudio();
    const [showTopMore, setShowTopMore] = useState(true);

    const { loading: getHistoryLoading } = useRequest(
        () =>
            getFeishuSessionsMsgHistory({
                params: {
                    sessionId: params.sessionId!,
                    startTime: params.startTime!,
                    endTime: params.endTime!,
                    pageSize: params.pageSize,
                    pageNum: params.pageNum,
                },
            }),
        {
            ready: mesgType === 'history' && !!params.sessionId,
            refreshDeps: [
                mesgType,
                params.sessionId,
                params.startTime,
                params.endTime,
                params.pageNum,
            ],
            onSuccess: (res) => {
                setData((d) => {
                    return [
                        ...(res.data || []).map((i) => ({
                            ...i,
                            playRudio: playRudio,
                            isPlaying: isPlaying,
                        })),
                        ...d,
                    ];
                });
                setShowTopMore(res.data?.length === params.pageSize);
            },
        }
    );

    const { loading: getTodayLoading } = useRequest(
        () =>
            getFeishuSessionsMsgToday({
                params: {
                    sessionId: params.sessionId!,
                },
            }),
        {
            ready: mesgType === 'today' && !!params.sessionId,
            refreshDeps: [mesgType, params.sessionId],
            onSuccess: (res) => {
                setData((d) => {
                    return [
                        ...(res.data || []).map((i) => ({
                            ...i,
                            playRudio: playRudio,
                            isPlaying: isPlaying,
                        })),
                        ...d,
                    ];
                });
                setShowTopMore(res.data?.length === params.pageSize);
            },
        }
    );

    useEffect(() => {
        setData((prev) => prev.map((d) => ({ ...d, isPlaying, playRudio })));
    }, [isPlaying, playRudio]);

    const loadMoreData = useCallback(() => {
        setParams((prev) => {
            return {
                ...prev,
                pageNum: prev.pageNum! + 1,
            };
        });
    }, []);

    const selectTime = (dateStrings) => {
        setParams((prev) => {
            return {
                ...prev,
                pageNum: 1,
                pageSize: 20,
                startTime: dateStrings[0],
                endTime: dateStrings[1],
            };
        });
        setData([]);
    };

    const { ref, height: h = 1 } = useResizeObserver<HTMLDivElement>();
    const height = data.length && showTopMore ? Math.max(h - 40, 0) : Math.max(h - 10, 0);

    const loading = useMemo(
        () => getHistoryLoading || getTodayLoading,
        [getHistoryLoading, getTodayLoading]
    );

    return (
        <div className="udesk-qa-web-page">
            <PageHeader
                className="udesk-qa-web-page-header"
                title={locales.pages.wechatQa.chartRecord.detail.title}
            />

            <div className="udesk-qa-web-page-body">
                <div className="udesk-qa-web-page-body-root wechat-qa-chart-record-detail">
                    <div className="wechat-qa-chart-record-detail-left-filter">
                        <Filter onChartChange={onChartChange} />
                    </div>

                    <div className="wechat-qa-chart-record-detail-main">
                        <div className="wechat-qa-chart-record-detail-left">
                            <div
                                style={{
                                    width: '100%',
                                    padding: '0 8px 16px',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    minHeight: 48,
                                }}
                            >
                                <Radio.Group
                                    value={mesgType}
                                    optionType="button"
                                    options={TABS_OPTIONS}
                                    onChange={onMesgTypeChange}
                                    disabled={loading}
                                ></Radio.Group>

                                <Space>
                                    {mesgType !== 'today' && (
                                        <UdeskDatePicker
                                            onChange={selectTime}
                                            value={[params.startTime!, params.endTime!]}
                                            allowClear={false}
                                            style={{ width: 255 }}
                                            disabled={loading}
                                        />
                                    )}
                                </Space>
                            </div>

                            <div
                                className="wechat-qa-chart-record-detail-left-chat-record"
                                id="containerDom"
                                ref={ref}
                            >
                                <ChatRecord
                                    data={data}
                                    loadMoreData={loadMoreData}
                                    height={height}
                                    showTopMore={showTopMore}
                                    loading={loading}
                                    virtual={false}
                                    isFeishu
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
});

export default Template;
